import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Search, Download, BookOpen, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { accountsApi } from '../../services/api/endpoints/accounts';
import { exportToExcel } from '../../utils/export/exportUtils';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../../stores/businessUnitStore';
import type { Account } from '../../types/accounting';

interface PlanComptableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAccount?: (account: Account) => void;
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  asset: 'Actif',
  liability: 'Passif',
  equity: 'Capitaux propres',
  revenue: 'Produit',
  expense: 'Charge',
};

const CLASS_LABELS: Record<string, string> = {
  '1': 'Classe 1 — Ressources durables',
  '2': 'Classe 2 — Actif immobilisé',
  '3': 'Classe 3 — Stocks',
  '4': 'Classe 4 — Tiers',
  '5': 'Classe 5 — Trésorerie',
  '6': 'Classe 6 — Charges',
  '7': 'Classe 7 — Produits',
  '8': 'Classe 8 — Autres charges/produits',
  '9': 'Classe 9 — Analytique',
};

const TYPE_BADGE_CLASSES: Record<string, string> = {
  asset: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  liability: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  equity: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  revenue: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  expense: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const PAGE_SIZE = 50;

export function PlanComptableModal({ isOpen, onClose, onSelectAccount }: PlanComptableModalProps) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [originFilter, setOriginFilter] = useState<string>('all'); // 'all' | 'standard' | 'custom'
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { currentFiscalYear } = useFiscalYearStore();
  const { effectiveFilter } = useBusinessUnitStore();

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Charger les comptes
  const fetchAccounts = useCallback(async (currentPage: number, searchQuery?: string) => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: currentPage,
        pageSize: PAGE_SIZE,
        sortBy: 'code',
        sortOrder: 'asc',
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType,
        fiscalYear: currentFiscalYear?.id,
      };

      if (searchQuery?.trim()) {
        params.search = searchQuery.trim();
      }
      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }
      if (classFilter !== 'all') {
        params.classCode = classFilter;
      }
      if (originFilter === 'standard') {
        params.standard = 'SYSCOHADA';
      }

      const response = await accountsApi.getAll(params);
      if (response.success && response.data) {
        setAccounts(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        setAccounts([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error loading plan comptable:', err);
      setAccounts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [effectiveFilter.businessUnitId, effectiveFilter.businessUnitType, currentFiscalYear?.id, typeFilter, classFilter, originFilter]);

  // Charger au montage et quand les filtres changent
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    fetchAccounts(1, search);
  }, [isOpen, typeFilter, classFilter, originFilter, fetchAccounts]);

  // Debounce la recherche
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (searchTimer) clearTimeout(searchTimer);
    const timer = setTimeout(() => {
      setPage(1);
      fetchAccounts(1, value);
    }, 400);
    setSearchTimer(timer);
  }, [fetchAccounts, searchTimer]);

  // Navigation de page
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchAccounts(newPage, search);
  }, [totalPages, search, fetchAccounts]);

  // Filtrer localement uniquement pour "personnalisé" (backend ne supporte pas "NOT standard")
  const displayedAccounts = useMemo(() => {
    let list = accounts;
    if (originFilter === 'custom') {
      list = list.filter(a => !isStandardAccount(a));
    }
    return list;
  }, [accounts, originFilter]);

  // Export XLSX
  const handleExportXLSX = useCallback(() => {
    const rows = displayedAccounts.map(a => ({
      Code: a.code || '',
      'Libellé': a.name || '',
      Type: ACCOUNT_TYPE_LABELS[a.type] || a.type || '',
      Classe: a.class || a.code?.charAt(0) || '',
      Origine: isStandardAccount(a) ? 'SYSCOHADA' : 'Personnalisé',
      Statut: a.active !== false ? 'Actif' : 'Inactif',
    }));
    exportToExcel(
      rows,
      ['Code', 'Libellé', 'Type', 'Classe', 'Origine', 'Statut'],
      `plan_comptable_${new Date().toISOString().slice(0, 10)}`
    );
  }, [displayedAccounts]);

  // Reset à la fermeture
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setTypeFilter('all');
      setClassFilter('all');
      setOriginFilter('all');
      setPage(1);
    }
  }, [isOpen]);

  const stats = useMemo(() => {
    const standard = accounts.filter(a => isStandardAccount(a)).length;
    const custom = accounts.length - standard;
    return { standard, custom, total };
  }, [accounts, total]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Plan Comptable" size="2xl" noPadding>
      <div className="flex flex-col h-[calc(100vh-12rem)] max-h-[700px]">
        {/* Barre d'outils */}
        <div className="px-4 py-3 border-b border-border space-y-3 flex-shrink-0">
          {/* Stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5" />
              <span>{total} comptes au total</span>
              <span className="text-border">|</span>
              <span className="text-blue-600 dark:text-blue-400">{stats.standard} normalisés</span>
              <span className="text-border">|</span>
              <span className="text-amber-600 dark:text-amber-400">{stats.custom} personnalisés</span>
            </div>
            <button
              onClick={handleExportXLSX}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              title="Exporter en XLSX"
            >
              <Download className="h-3 w-3" />
              XLSX
            </button>
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher code ou libellé..."
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border rounded-md text-xs bg-background border-border focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
            <select
              value={classFilter}
              onChange={e => setClassFilter(e.target.value)}
              className="px-2 py-1.5 border rounded-md text-xs bg-background border-border"
            >
              <option value="all">Toutes classes</option>
              {Object.entries(CLASS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-2 py-1.5 border rounded-md text-xs bg-background border-border"
            >
              <option value="all">Tous types</option>
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={originFilter}
              onChange={e => setOriginFilter(e.target.value)}
              className="px-2 py-1.5 border rounded-md text-xs bg-background border-border"
            >
              <option value="all">Toute origine</option>
              <option value="standard">Normalisés SYSCOHADA</option>
              <option value="custom">Personnalisés</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : displayedAccounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <Filter className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">Aucun compte trouvé</p>
              <p className="text-xs mt-1">Ajustez vos filtres ou votre recherche</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[100px]">Code</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Libellé</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[100px]">Type</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground w-[60px]">Classe</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground w-[110px]">Origine</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground w-[70px]">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayedAccounts.map((account) => {
                  const isStd = isStandardAccount(account);
                  return (
                    <tr
                      key={account.id}
                      className={`hover:bg-muted/30 transition-colors ${onSelectAccount ? 'cursor-pointer' : ''}`}
                      onClick={() => onSelectAccount?.(account)}
                    >
                      <td className="px-3 py-1.5">
                        <span className="font-mono text-[11px] bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                          {account.code}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-foreground truncate max-w-[300px]" title={account.name}>
                        {account.name}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${TYPE_BADGE_CLASSES[account.type] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}>
                          {ACCOUNT_TYPE_LABELS[account.type] || account.type}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className="font-mono text-muted-foreground">
                          {account.class || account.code?.charAt(0) || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                          isStd
                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                            : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                        }`}>
                          {isStd ? 'SYSCOHADA' : 'Personnalisé'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          account.active !== false ? 'bg-green-500' : 'bg-gray-400'
                        }`} title={account.active !== false ? 'Actif' : 'Inactif'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-border flex-shrink-0 bg-muted/30">
            <span className="text-xs text-muted-foreground">
              Page {page} / {totalPages} — {displayedAccounts.length} affichés sur {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              {/* Page numbers */}
              {getPageNumbers(page, totalPages).map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p as number)}
                    className={`px-2 py-0.5 text-xs rounded ${
                      p === page
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Déterminer si un compte est un compte normalisé SYSCOHADA
 * Un compte standard a un code de 1-4 chiffres qui correspond au plan normalisé,
 * ou a le champ `standard` explicitement défini.
 * Les comptes personnalisés par l'entreprise ont habituellement un code plus
 * détaillé (5-6 chiffres) sans correspondance exacte dans le plan standard,
 * ou ont `standard` absent/différent.
 */
function isStandardAccount(account: Account): boolean {
  // Si le backend fournit explicitement le champ standard
  if (account.standard) {
    return account.standard === 'SYSCOHADA' || account.standard === 'IFRS';
  }
  // Heuristique : les comptes normalisés ont typiquement 1-4 chiffres
  // Les comptes détaillés (5-6 chiffres) sont souvent des sous-comptes personnalisés
  const code = account.code || '';
  return code.length <= 4;
}

/**
 * Générer les numéros de pages à afficher dans la pagination
 */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  
  const pages: (number | '...')[] = [];
  
  if (current <= 3) {
    pages.push(1, 2, 3, 4, '...', total);
  } else if (current >= total - 2) {
    pages.push(1, '...', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }
  
  return pages;
}
